/**
 * Pure helpers for the shared dropdown (ui/Select).
 * No React, no DOM — safe to test with Vitest directly.
 */

/** Next index with optional wrapping. */
export function nextIndex(
  current: number,
  delta: number,
  count: number,
  wrap: boolean,
): number {
  if (count === 0) return -1;
  const next = current + delta;
  if (wrap) {
    return ((next % count) + count) % count;
  }
  return Math.max(0, Math.min(count - 1, next));
}

/** First option whose label starts with `query`, searching from `from + 1` and wrapping around. Case-insensitive. */
export function typeaheadIndex(
  options: readonly { label: string }[],
  query: string,
  from: number,
): number {
  if (options.length === 0 || !query) return -1;
  const q = query.toLowerCase();
  for (let i = 0; i < options.length; i++) {
    const idx = (from + 1 + i) % options.length;
    if (options[idx].label.toLowerCase().startsWith(q)) return idx;
  }
  return -1;
}

/**
 * Filter options whose label contains `query` (case- and accent-insensitive).
 * Returns the original objects so the caller can map back to indices.
 */
export function filterOptions<T extends { label: string }>(
  options: readonly T[],
  query: string,
): T[] {
  if (!query) return options as T[];
  const q = normalizeForSearch(query);
  return options.filter((o) => normalizeForSearch(o.label).includes(q));
}

/**
 * Normalizes a string for case- and accent-insensitive exact matching.
 */
function normalizeForSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Returns true if the query is not empty and no option exactly matches the query
 * (case- and accent-insensitive).
 */
export function canOfferCreate(
  options: readonly { label: string }[],
  query: string,
): boolean {
  if (!query.trim()) return false;
  const q = normalizeForSearch(query.trim());
  return !options.some((o) => normalizeForSearch(o.label) === q);
}

/** Show a search input when there are more than 6 options (owner, 2026-11-03: long lists must always be searchable). */
export function shouldShowSearch(count: number): boolean {
  return count > 6;
}

/**
 * Decide whether the menu should open downward or upward.
 * Opens upward only when there isn't enough room below the trigger.
 */
export function placement(
  triggerRect: { bottom: number; top: number },
  menuHeight: number,
  viewportHeight: number,
): "down" | "up" {
  const spaceBelow = viewportHeight - triggerRect.bottom;
  if (spaceBelow >= menuHeight) return "down";
  const spaceAbove = triggerRect.top;
  if (spaceAbove > spaceBelow) return "up";
  return "down";
}
